import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast, AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader, AlertDialogContent, AlertDialogOverlay, Button } from '@chakra-ui/react';
import { personalTokenService, PersonalToken, CreateTokenResponse } from '../../services/personalTokenService';
import { formatDistanceToNow } from 'date-fns';
import { zhCN, enUS } from 'date-fns/locale';
import { useAtom, useAtomValue } from 'jotai';
import { SettingsPageLayout } from './components/layout/SettingsPageLayout';
import {
  currentLanguageAtom,
  whisperForceCurrentLanguageAtom,
  themeModeAtom,
  systemThemeAtom,
  isDarkAtom,
  effectiveThemeAtom,
  activeVersionAtom
} from '../../atoms/shared/uiAtoms';
import {
  assistiveTouchVisibleAtom,
  assistiveTouchPositionAtom
} from '../../atoms/assistiveTouchAtoms';
import {
  debugPanelVisibleAtom,
  useFrontendLogger
} from '../../atoms/v3/debugAtoms';
import { clearPWACache, forceUpdatePWA, checkForPWAUpdate } from '../../utils/pwaUtils';
import { getPlatformInfo, getCurrentRemConfig, getComputedRootFontSize, getPwaStatus, type PlatformInfo } from '../../utils/platformUtils';
import { ChatMessage } from './components/chatMessage/ChatMessage';
import { projectsAtom, tasksAtom } from '../../atoms/v0';
import { PermissionOverview } from '../../components/v3/PermissionOverview';
import { MailgunTestPanel } from '../../components/v3/MailgunTestPanel';
import { PushyTestPanel } from '../../components/v3/PushyTestPanel';
import type { ElectronNetworkTestResult, ElectronNetworkStatus } from '../../types/electron';

/**
 * 开发者设置页面
 * 用于管理个人访问令牌和查看API配置示例
 */
export const DeveloperSettings: React.FC = () => {
  const { t } = useTranslation(['v3/me', 'v3/developerSettings']);
  const navigate = useNavigate();
  const toast = useToast();
  const currentLanguage = useAtomValue(currentLanguageAtom);
  const [forceWhisperLanguage, setForceWhisperLanguage] = useAtom(whisperForceCurrentLanguageAtom);

  // 测试小圆球状态
  const [assistiveTouchVisible, setAssistiveTouchVisible] = useAtom(assistiveTouchVisibleAtom);
  const [assistiveTouchPosition, setAssistiveTouchPosition] = useAtom(assistiveTouchPositionAtom);

  // 调试面板状态
  const [debugPanelVisible, setDebugPanelVisible] = useAtom(debugPanelVisibleAtom);

  // 前端日志记录器
  const logger = useFrontendLogger();

  // 主题状态监控
  const themeMode = useAtomValue(themeModeAtom);
  const systemTheme = useAtomValue(systemThemeAtom);
  const isDark = useAtomValue(isDarkAtom);
  const effectiveTheme = useAtomValue(effectiveThemeAtom);
  const activeVersion = useAtomValue(activeVersionAtom);

  // 获取真实的项目和任务数据
  const projects = useAtomValue(projectsAtom);
  const tasks = useAtomValue(tasksAtom);

  // 状态
  const [tokens, setTokens] = useState<PersonalToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [newTokenName, setNewTokenName] = useState('');
  const [creatingToken, setCreatingToken] = useState(false);
  const [newToken, setNewToken] = useState<CreateTokenResponse | null>(null);
  const [deletingTokenId, setDeletingTokenId] = useState<string | null>(null);

  // 缓存管理状态
  const [isCheckingUpdate, setIsCheckingUpdate] = useState(false);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'clearCache' | 'clearLocalStorage' | 'clearSpecific' | null>(null);
  const [specificStorageKey, setSpecificStorageKey] = useState<string | null>(null);
  const cancelRef = React.useRef<HTMLButtonElement>(null);

  // 平台信息状态
  const [platformInfo, setPlatformInfo] = useState<PlatformInfo | null>(null);
  const [remConfig, setRemConfig] = useState<number>(14);
  const [computedFontSize, setComputedFontSize] = useState<number>(14);
  const [pwaStatus, setPwaStatus] = useState<any>(null);

  // 网络测试状态
  const [testUrl, setTestUrl] = useState<string>('https://www.google.com');
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [lastTestResult, setLastTestResult] = useState<ElectronNetworkTestResult | null>(null);
  const [isCheckingNetworkStatus, setIsCheckingNetworkStatus] = useState(false);
  const [networkStatus, setNetworkStatus] = useState<ElectronNetworkStatus | null>(null);
  const [lastNetworkCheck, setLastNetworkCheck] = useState<Date | null>(null);

  // 加载令牌列表
  const loadTokens = async () => {
    try {
      setLoading(true);
      const data = await personalTokenService.getUserTokens();
      setTokens(data);
    } catch (error) {
      console.error('获取个人令牌失败:', error);
      toast({
        title: t('v3/me:developer.tokenLoadFailed'),
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top'
      });
    } finally {
      setLoading(false);
    }
  };

  // 初始加载
  useEffect(() => {
    loadTokens();

    // 初始化平台信息
    const initPlatformInfo = async () => {
      const info = getPlatformInfo();
      setPlatformInfo(info);

      const currentRem = getCurrentRemConfig();
      setRemConfig(currentRem);

      const computed = getComputedRootFontSize();
      setComputedFontSize(computed);

      // 获取 PWA 状态
      try {
        const pwaInfo = await getPwaStatus();
        setPwaStatus(pwaInfo);
      } catch (error) {
        console.error('Failed to get PWA status:', error);
      }
    };

    initPlatformInfo();

    // 设置定时器，每秒更新一次字体大小信息
    const interval = setInterval(() => {
      const currentRem = getCurrentRemConfig();
      setRemConfig(currentRem);

      const computed = getComputedRootFontSize();
      setComputedFontSize(computed);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // 创建新令牌
  const handleCreateToken = async () => {
    if (!newTokenName.trim()) {
      toast({
        title: t('v3/me:developer.tokenNameRequired'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
        position: 'top'
      });
      return;
    }

    try {
      setCreatingToken(true);
      const response = await personalTokenService.createToken(newTokenName.trim());
      setNewToken(response);
      setNewTokenName('');
      await loadTokens(); // 重新加载令牌列表

      toast({
        title: t('v3/me:developer.tokenCreated'),
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top'
      });
    } catch (error) {
      console.error('创建个人令牌失败:', error);
      toast({
        title: t('v3/me:developer.tokenCreateFailed'),
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top'
      });
    } finally {
      setCreatingToken(false);
    }
  };

  // 删除令牌
  const handleDeleteToken = async (tokenId: string) => {
    try {
      setDeletingTokenId(tokenId);
      await personalTokenService.deleteToken(tokenId);
      setTokens(tokens.filter(token => token.id !== tokenId));

      toast({
        title: t('v3/me:developer.tokenDeleted'),
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top'
      });
    } catch (error) {
      console.error('删除个人令牌失败:', error);
      toast({
        title: t('v3/me:developer.tokenDeleteFailed'),
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top'
      });
    } finally {
      setDeletingTokenId(null);
    }
  };

  // 复制令牌到剪贴板
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(
      () => {
        toast({
          title: t('v3/me:developer.copied'),
          status: 'success',
          duration: 2000,
          isClosable: true,
          position: 'top'
        });
      },
      (err) => {
        console.error('复制失败:', err);
        toast({
          title: t('v3/me:developer.copyFailed'),
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top'
        });
      }
    );
  };

  // 格式化日期
  const formatDate = (dateString: string | null) => {
    if (!dateString) return t('v3/me:developer.never');

    const date = new Date(dateString);
    const locale = currentLanguage === 'zh' ? zhCN : enUS;

    return formatDistanceToNow(date, {
      addSuffix: true,
      locale
    });
  };

  // MCP配置示例
  const mcpConfigExample = `{
  "mcpServers": {
    "tempo-mcp": {
      "command": "npx",
      "args": [
        "-y",
        "mcp-remote",
        "http://localhost:3099/mcp/httpStream",
        "--header",
        "Authorization:Personal ${t('v3/me:developer.yourTokenHere')}"
      ]
    }
  }
}`;

  // 切换强制 Whisper 使用当前语言设置
  const toggleWhisperLanguage = () => {
    setForceWhisperLanguage(!forceWhisperLanguage);
    toast({
      title: forceWhisperLanguage
        ? t('v3/me:developer.whisperAutoDetectEnabled')
        : t('v3/me:developer.whisperForceLanguageEnabled'),
      status: 'success',
      duration: 2000,
      isClosable: true,
      position: 'top'
    });
  };

  // 切换测试小圆球显示状态
  const toggleAssistiveTouch = () => {
    setAssistiveTouchVisible(!assistiveTouchVisible);
    toast({
      title: t('v3/developerSettings:assistiveTouch.toggleSuccess'),
      status: 'success',
      duration: 2000,
      isClosable: true,
      position: 'top'
    });
  };

  // 重置测试小圆球位置
  const resetAssistiveTouchPosition = () => {
    setAssistiveTouchPosition({
      x: 20, // 默认位置 - 距离右侧20px
      y: 300, // 默认位置 - 距离顶部300px
    });
    toast({
      title: t('v3/developerSettings:assistiveTouch.positionReset'),
      status: 'success',
      duration: 2000,
      isClosable: true,
      position: 'top'
    });
  };

  // 切换调试面板显示状态
  const toggleDebugPanel = () => {
    setDebugPanelVisible(!debugPanelVisible);
    toast({
      title: debugPanelVisible
        ? '调试面板已隐藏'
        : '调试面板已显示',
      status: 'success',
      duration: 2000,
      isClosable: true,
      position: 'top'
    });
  };

  // 检查 PWA 更新
  const handleCheckUpdate = async () => {
    setIsCheckingUpdate(true);
    try {
      await checkForPWAUpdate();
      setLastChecked(new Date());
      toast({
        title: t('v3/me:developer.checkComplete'),
        description: t('v3/me:developer.updateNotAvailable'),
        status: 'info',
        duration: 3000,
        isClosable: true,
        position: 'top'
      });
    } catch (error) {
      console.error('检查更新失败:', error);
      toast({
        title: t('v3/me:developer.updateFailed'),
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top'
      });
    } finally {
      setIsCheckingUpdate(false);
    }
  };

  // 强制更新 PWA
  const handleForceUpdate = async () => {
    try {
      toast({
        title: t('v3/me:developer.updating'),
        description: t('v3/me:developer.clearingCache'),
        status: 'loading',
        duration: 5000,
        isClosable: false,
        position: 'top'
      });
      await forceUpdatePWA();
    } catch (error) {
      console.error('强制更新失败:', error);
      toast({
        title: t('v3/me:developer.updateFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top'
      });
    }
  };

  // 清除 PWA 缓存
  const handleClearCache = async () => {
    setIsConfirmDialogOpen(false);
    try {
      const cleared = await clearPWACache();
      if (cleared) {
        toast({
          title: t('v3/me:developer.cacheCleared'),
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top'
        });
      } else {
        toast({
          title: t('v3/me:developer.cacheClearFailed'),
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top'
        });
      }
    } catch (error) {
      console.error('清除缓存失败:', error);
      toast({
        title: t('v3/me:developer.cacheClearFailed'),
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top'
      });
    }
  };

  // 清除所有 localStorage
  const handleClearLocalStorage = () => {
    setIsConfirmDialogOpen(false);
    try {
      localStorage.clear();
      toast({
        title: t('v3/me:developer.storageCleared'),
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top'
      });
    } catch (error) {
      console.error('清除本地存储失败:', error);
      toast({
        title: t('v3/me:developer.storageClearFailed'),
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top'
      });
    }
  };

  // 清除特定 localStorage 键
  const handleClearSpecificStorage = (key: string) => {
    setIsConfirmDialogOpen(false);
    try {
      localStorage.removeItem(key);
      toast({
        title: t('v3/me:developer.storageCleared'),
        description: `${key}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top'
      });
    } catch (error) {
      console.error(`清除存储键 ${key} 失败:`, error);
      toast({
        title: t('v3/me:developer.storageClearFailed'),
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top'
      });
    }
  };

  // 打开确认对话框
  const openConfirmDialog = (action: 'clearCache' | 'clearLocalStorage' | 'clearSpecific', key?: string) => {
    setConfirmAction(action);
    if (key) {
      setSpecificStorageKey(key);
    }
    setIsConfirmDialogOpen(true);
  };

  // 处理确认操作
  const handleConfirmAction = () => {
    if (confirmAction === 'clearCache') {
      handleClearCache();
    } else if (confirmAction === 'clearLocalStorage') {
      handleClearLocalStorage();
    } else if (confirmAction === 'clearSpecific' && specificStorageKey) {
      handleClearSpecificStorage(specificStorageKey);
    }
  };

  // 检测是否在 Electron 环境中
  const isElectron = typeof window !== 'undefined' && window.electronNetwork;

  // 测试网络连接
  const handleTestConnection = async () => {
    if (!isElectron) {
      toast({
        title: t('v3/developerSettings:networkTest.electronOnly'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
        position: 'top'
      });
      return;
    }

    if (!testUrl.trim()) {
      toast({
        title: t('v3/developerSettings:networkTest.urlPlaceholder'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
        position: 'top'
      });
      return;
    }

    setIsTestingConnection(true);
    try {
      const result = await window.electronNetwork!.testConnection(testUrl);
      setLastTestResult(result);

      toast({
        title: result.success
          ? t('v3/developerSettings:networkTest.success')
          : t('v3/developerSettings:networkTest.failed'),
        description: result.success
          ? `${t('v3/developerSettings:networkTest.status')}: ${result.status}`
          : result.error,
        status: result.success ? 'success' : 'error',
        duration: 5000,
        isClosable: true,
        position: 'top'
      });
    } catch (error) {
      console.error('网络连接测试失败:', error);
      toast({
        title: t('v3/developerSettings:networkTest.failed'),
        description: error instanceof Error ? error.message : '未知错误',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top'
      });
    } finally {
      setIsTestingConnection(false);
    }
  };

  // 检查网络状态
  const handleCheckNetworkStatus = async () => {
    if (!isElectron) {
      toast({
        title: t('v3/developerSettings:networkTest.electronOnly'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
        position: 'top'
      });
      return;
    }

    setIsCheckingNetworkStatus(true);
    try {
      const status = await window.electronNetwork!.getNetworkStatus();
      setNetworkStatus(status);
      setLastNetworkCheck(new Date());

      toast({
        title: t('v3/developerSettings:networkTest.networkStatus'),
        description: status.online
          ? `${t('v3/developerSettings:networkTest.online')} (${status.successCount}/${status.totalTests})`
          : t('v3/developerSettings:networkTest.offline'),
        status: status.online ? 'success' : 'error',
        duration: 3000,
        isClosable: true,
        position: 'top'
      });
    } catch (error) {
      console.error('网络状态检查失败:', error);
      toast({
        title: t('v3/developerSettings:networkTest.failed'),
        description: error instanceof Error ? error.message : '未知错误',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top'
      });
    } finally {
      setIsCheckingNetworkStatus(false);
    }
  };

  // 生成测试消息数据
  const generateTestMessages = () => {
    // 随机选择一些项目和任务
    const sampleProjects = projects.slice(0, 3);
    const sampleTasks = tasks.slice(0, 3);

    const messages = [];

    // 项目链接测试消息
    if (sampleProjects.length > 0) {
      const projectLinks = sampleProjects.map(p => `[${p.title}](tempo://project/${p.id})`).join(' 和 ');
      messages.push({
        id: 'test-project-real',
        role: 'assistant' as const,
        content: `这些是真实的项目：${projectLinks}`,
        conversationId: 'test-conv',
        timestamp: new Date().toISOString(),
      });
    }

    // 任务链接测试消息
    if (sampleTasks.length > 0) {
      const taskLinks = sampleTasks.map(t => `[${t.title}](tempo://task/${t.id})`).join(' 和 ');
      messages.push({
        id: 'test-task-real',
        role: 'assistant' as const,
        content: `相关任务：${taskLinks}`,
        conversationId: 'test-conv',
        timestamp: new Date().toISOString(),
      });
    }

    // 混合链接测试消息
    if (sampleProjects.length > 0 && sampleTasks.length > 0) {
      const mixedContent = `## 工作总结

已完成以下工作：
- [${sampleProjects[0]?.title || '项目1'}](tempo://project/${sampleProjects[0]?.id || 'test'}) ✓
- [${sampleTasks[0]?.title || '任务1'}](tempo://task/${sampleTasks[0]?.id || 'test'}) ✓

下一步计划：
1. [${sampleTasks[1]?.title || '任务2'}](tempo://task/${sampleTasks[1]?.id || 'test'})
2. [${sampleProjects[1]?.title || '项目2'}](tempo://project/${sampleProjects[1]?.id || 'test'})

还有一个普通链接：[GitHub](https://github.com) 用于对比。`;

      messages.push({
        id: 'test-mixed-real',
        role: 'assistant' as const,
        content: mixedContent,
        conversationId: 'test-conv',
        timestamp: new Date().toISOString(),
      });
    }

    // 如果没有真实数据，使用示例数据
    if (messages.length === 0) {
      messages.push({
        id: 'test-fallback',
        role: 'assistant' as const,
        content: '暂无真实项目和任务数据。请先创建一些项目和任务，然后刷新页面查看真实的 tempo:// 链接测试。\n\n示例链接：[示例项目](tempo://project/123e4567-e89b-12d3-a456-426614174000) 和 [示例任务](tempo://task/456e7890-e89b-12d3-a456-426614174001)',
        conversationId: 'test-conv',
        timestamp: new Date().toISOString(),
      });
    }

    return messages;
  };

  return (
    <SettingsPageLayout
      title={t('v3/me:developer.title')}
      onBack={() => navigate('/v3/me')}
    >

      {/* 测试小圆球控制区域 */}
      <div className="mb-6">
        <div className="bg-white dark:bg-ios-dark-gray6 rounded-ios-lg overflow-hidden border border-ios-gray5 dark:border-ios-dark-gray5 shadow-ios dark:shadow-ios-dark p-4">
          <h2 className="text-ios-title3 font-semibold mb-3">{t('v3/developerSettings:assistiveTouch.title')}</h2>
          <p className="text-ios-subhead text-ios-gray dark:text-ios-dark-gray mb-4">
            {t('v3/developerSettings:assistiveTouch.description')}
          </p>

          {/* 显示开关 */}
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-ios-headline font-semibold">{t('v3/developerSettings:assistiveTouch.showAssistiveTouch')}</h3>
              <p className="text-ios-subhead text-ios-gray dark:text-ios-dark-gray">
                {assistiveTouchVisible ? t('v3/developerSettings:assistiveTouch.currentlyVisible') : t('v3/developerSettings:assistiveTouch.currentlyHidden')}
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={assistiveTouchVisible}
                onChange={toggleAssistiveTouch}
              />
              <div className="w-11 h-6 bg-ios-gray5 peer-focus:ring-4 peer-focus:ring-ios-blue/20 dark:peer-focus:ring-ios-dark-blue/20 rounded-full peer dark:bg-ios-dark-gray4 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-ios-blue dark:peer-checked:bg-ios-dark-blue"></div>
            </label>
          </div>

          {/* 位置重置按钮 */}
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-ios-headline font-semibold">{t('v3/developerSettings:assistiveTouch.resetPosition')}</h3>
              <p className="text-ios-subhead text-ios-gray dark:text-ios-dark-gray">
                {t('v3/developerSettings:assistiveTouch.currentPosition', { x: assistiveTouchPosition.x, y: assistiveTouchPosition.y })}
              </p>
            </div>
            <button
              onClick={resetAssistiveTouchPosition}
              className="px-4 py-2 bg-ios-orange dark:bg-ios-dark-orange text-white rounded-ios-md text-ios-subhead font-medium hover:bg-ios-orange-dark dark:hover:bg-ios-dark-orange-dark transition-colors"
            >
              {t('v3/developerSettings:assistiveTouch.resetPosition')}
            </button>
          </div>
        </div>
      </div>

      {/* 调试面板控制区域 */}
      <div className="mb-6">
        <div className="bg-white dark:bg-ios-dark-gray6 rounded-ios-lg overflow-hidden border border-ios-gray5 dark:border-ios-dark-gray5 shadow-ios dark:shadow-ios-dark p-4">
          <h2 className="text-ios-title3 font-semibold mb-3">🔧 调试面板</h2>
          <p className="text-ios-subhead text-ios-gray dark:text-ios-dark-gray mb-4">
            前端专用调试面板，只在浏览器中显示日志，不发送到后端或 Sentry。支持 info、warn、error、debug 四种级别。
          </p>

          {/* 显示开关 */}
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-ios-headline font-semibold">显示调试面板</h3>
              <p className="text-ios-subhead text-ios-gray dark:text-ios-dark-gray">
                {debugPanelVisible ? '当前显示中' : '当前已隐藏'}
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={debugPanelVisible}
                onChange={toggleDebugPanel}
              />
              <div className="w-11 h-6 bg-ios-gray5 peer-focus:ring-4 peer-focus:ring-ios-blue/20 dark:peer-focus:ring-ios-dark-blue/20 rounded-full peer dark:bg-ios-dark-gray4 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-ios-blue dark:peer-checked:bg-ios-dark-blue"></div>
            </label>
          </div>

          {/* 测试按钮 */}
          <div>
            <h3 className="text-ios-headline font-semibold mb-2">测试前端日志</h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => logger.info('这是一条信息日志', { timestamp: new Date().toISOString() })}
                className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-ios-md text-ios-subhead font-medium"
              >
                测试 Info
              </button>
              <button
                onClick={() => logger.warn('这是一条警告日志', { level: 'warning', source: 'test' })}
                className="px-3 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-ios-md text-ios-subhead font-medium"
              >
                测试 Warn
              </button>
              <button
                onClick={() => logger.error('这是一条错误日志', new Error('测试错误'), { context: 'developer-settings' })}
                className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-ios-md text-ios-subhead font-medium"
              >
                测试 Error
              </button>
              <button
                onClick={() => logger.debug('这是一条调试日志', { debugInfo: { component: 'DeveloperSettings', action: 'test' } })}
                className="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-ios-md text-ios-subhead font-medium"
              >
                测试 Debug
              </button>
              <button
                onClick={() => logger.clear()}
                className="px-3 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-ios-md text-ios-subhead font-medium"
              >
                清空日志
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 快速跳转区域 */}
      <div className="mb-6">
        <div className="bg-white dark:bg-ios-dark-gray6 rounded-ios-lg overflow-hidden border border-ios-gray5 dark:border-ios-dark-gray5 shadow-ios dark:shadow-ios-dark p-4">
          <h2 className="text-ios-title3 font-semibold mb-3">{t('v3/developerSettings:quickJump.title')}</h2>
          <button
            onClick={() => window.location.href = '/v0/notification-test'}
            className="w-full px-4 py-3 bg-ios-blue dark:bg-ios-dark-blue text-white rounded-ios-md text-ios-body font-medium hover:bg-ios-blue-dark dark:hover:bg-ios-dark-blue-dark transition-colors"
          >
            {t('v3/developerSettings:quickJump.goToNotificationTest')}
          </button>
        </div>
      </div>

      {/* Mailgun 邮件测试区域 */}
      <MailgunTestPanel />

      {/* Pushy 推送测试区域 */}
      <PushyTestPanel />

      {/* 平台信息 */}
      <div className="mb-6">
        <div className="bg-white dark:bg-ios-dark-gray6 rounded-ios-lg overflow-hidden border border-ios-gray5 dark:border-ios-dark-gray5 shadow-ios dark:shadow-ios-dark p-4">
          <h2 className="text-ios-title3 font-semibold mb-3">{t('v3/developerSettings:platformInfo.title')}</h2>

          {/* 原始平台信息 */}
          <div className="mb-4">
            <h3 className="text-ios-headline font-medium mb-2">{t('v3/developerSettings:platformInfo.fullInfo')}</h3>
            <div className="bg-ios-gray6 dark:bg-ios-dark-gray5 rounded-ios-md p-3">
              <pre className="text-ios-footnote font-mono text-ios-black dark:text-white overflow-x-auto whitespace-pre-wrap">
                {platformInfo ? JSON.stringify(platformInfo, null, 2) : t('v3/developerSettings:platformInfo.loadingText')}
              </pre>
            </div>
          </div>

          {/* PWA 状态信息 */}
          <div className="mb-4">
            <h3 className="text-ios-headline font-medium mb-2">{t('v3/developerSettings:platformInfo.pwaStatus')}</h3>
            <div className="bg-ios-gray6 dark:bg-ios-dark-gray5 rounded-ios-md p-3">
              <pre className="text-ios-footnote font-mono text-ios-black dark:text-white overflow-x-auto whitespace-pre-wrap">
                {pwaStatus ? JSON.stringify(pwaStatus, null, 2) : t('v3/developerSettings:platformInfo.loadingText')}
              </pre>
            </div>
          </div>

          {/* rem 配置信息 */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-ios-headline font-medium">{t('v3/developerSettings:platformInfo.remConfig')}</h3>
              <button
                onClick={() => {
                  const currentRem = getCurrentRemConfig();
                  setRemConfig(currentRem);
                  const computed = getComputedRootFontSize();
                  setComputedFontSize(computed);
                  const info = getPlatformInfo();
                  setPlatformInfo(info);
                }}
                className="px-3 py-1 bg-ios-blue dark:bg-ios-dark-blue text-white rounded-ios-sm text-ios-caption1 hover:bg-ios-blue-dark dark:hover:bg-ios-dark-blue-dark transition-colors"
              >
                {t('v3/developerSettings:platformInfo.refresh')}
              </button>
            </div>
            <div className="bg-ios-gray6 dark:bg-ios-dark-gray5 rounded-ios-md p-3">
              <div className="text-ios-subhead">
                <div className="mb-1">
                  <span className="text-ios-gray dark:text-ios-dark-gray">{t('v3/developerSettings:platformInfo.configValue')}{t('v3/developerSettings:common.units.colon')}</span>
                  <span className="font-medium">{remConfig}{t('v3/developerSettings:common.units.px')}</span>
                </div>
                <div className="mb-1">
                  <span className="text-ios-gray dark:text-ios-dark-gray">{t('v3/developerSettings:platformInfo.actualValue')}{t('v3/developerSettings:common.units.colon')}</span>
                  <span className="font-medium">{computedFontSize.toFixed(1)}{t('v3/developerSettings:common.units.px')}</span>
                </div>
                <div className="mb-1">
                  <span className="text-ios-gray dark:text-ios-dark-gray">{t('v3/developerSettings:platformInfo.htmlClass')}{t('v3/developerSettings:common.units.colon')}</span>
                  <span className="font-medium">
                    {typeof document !== 'undefined' ?
                      Array.from(document.documentElement.classList).join(', ') :
                      t('v3/developerSettings:platformInfo.unknown')
                    }
                  </span>
                </div>
                <div className="mb-1">
                  <span className="text-ios-gray dark:text-ios-dark-gray">{t('v3/developerSettings:platformInfo.capacitorPlatform')}{t('v3/developerSettings:common.units.colon')}</span>
                  <span className="font-medium">{platformInfo?.os || t('v3/developerSettings:platformInfo.unknown')}</span>
                </div>
                <div className="mb-1">
                  <span className="text-ios-gray dark:text-ios-dark-gray">{t('v3/developerSettings:platformInfo.userAgent')}{t('v3/developerSettings:common.units.colon')}</span>
                  <span className="font-medium text-xs break-all">{platformInfo?.userAgent || t('v3/developerSettings:platformInfo.unknown')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* 主题状态监控 */}
          <div>
            <h3 className="text-ios-headline font-medium mb-2">{t('v3/developerSettings:themeMonitor.title')}</h3>
            <div className="bg-ios-gray6 dark:bg-ios-dark-gray5 rounded-ios-md p-3">
              <div className="text-ios-subhead space-y-2">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-ios-gray dark:text-ios-dark-gray">{t('v3/developerSettings:themeMonitor.userSetting')}{t('v3/developerSettings:common.units.colon')}</span>
                    <span className="font-medium ml-1">{themeMode}</span>
                  </div>
                  <div>
                    <span className="text-ios-gray dark:text-ios-dark-gray">{t('v3/developerSettings:themeMonitor.systemTheme')}{t('v3/developerSettings:common.units.colon')}</span>
                    <span className="font-medium ml-1">{systemTheme}</span>
                  </div>
                  <div>
                    <span className="text-ios-gray dark:text-ios-dark-gray">{t('v3/developerSettings:themeMonitor.currentTheme')}{t('v3/developerSettings:common.units.colon')}</span>
                    <span className="font-medium ml-1">{effectiveTheme}</span>
                  </div>
                  <div>
                    <span className="text-ios-gray dark:text-ios-dark-gray">{t('v3/developerSettings:themeMonitor.darkMode')}{t('v3/developerSettings:common.units.colon')}</span>
                    <span className="font-medium ml-1">{isDark ? 'true' : 'false'}</span>
                  </div>
                  <div>
                    <span className="text-ios-gray dark:text-ios-dark-gray">{t('v3/developerSettings:themeMonitor.currentVersion')}{t('v3/developerSettings:common.units.colon')}</span>
                    <span className="font-medium ml-1">{activeVersion}</span>
                  </div>
                  <div>
                    <span className="text-ios-gray dark:text-ios-dark-gray">{t('v3/developerSettings:platformInfo.htmlClass')}{t('v3/developerSettings:common.units.colon')}</span>
                    <span className="font-medium ml-1">
                      {typeof document !== 'undefined' && document.documentElement.classList.contains('dark') ? 'dark' : 'light'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 客户端权限总览 */}
          <PermissionOverview />
        </div>
      </div>

      {/* 网络连接测试部分 */}
      <div className="mb-6">
        <h2 className="text-ios-title3 font-semibold mb-2">{t('v3/developerSettings:networkTest.title')}</h2>
        <div className="bg-white dark:bg-ios-dark-gray6 rounded-ios-lg overflow-hidden border border-ios-gray5 dark:border-ios-dark-gray5 shadow-ios dark:shadow-ios-dark p-4">
          <p className="text-ios-subhead text-ios-gray dark:text-ios-dark-gray mb-4">
            {t('v3/developerSettings:networkTest.description')}
          </p>

          {!isElectron && (
            <div className="mb-4 p-3 bg-ios-orange/10 dark:bg-ios-dark-orange/10 border border-ios-orange/20 dark:border-ios-dark-orange/20 rounded-ios-md">
              <p className="text-ios-subhead text-ios-orange dark:text-ios-dark-orange">
                {t('v3/developerSettings:networkTest.electronOnly')}
              </p>
            </div>
          )}

          {/* 网络状态检查 */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-ios-headline font-medium">{t('v3/developerSettings:networkTest.networkStatus')}</h3>
              <button
                onClick={handleCheckNetworkStatus}
                disabled={isCheckingNetworkStatus || !isElectron}
                className="px-3 py-2 bg-ios-blue dark:bg-ios-dark-blue text-white rounded-ios-md text-ios-subhead font-medium disabled:opacity-50"
              >
                {isCheckingNetworkStatus ? t('v3/developerSettings:networkTest.checking') : t('v3/developerSettings:networkTest.refreshStatus')}
              </button>
            </div>

            {networkStatus && (
              <div className="bg-ios-gray6 dark:bg-ios-dark-gray5 rounded-ios-md p-3">
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <span className="text-ios-gray dark:text-ios-dark-gray">{t('v3/developerSettings:networkTest.onlineStatus')}{t('v3/developerSettings:common.units.colon')}</span>
                    <span className={`font-medium ml-1 ${networkStatus.online ? 'text-ios-green dark:text-ios-dark-green' : 'text-ios-red dark:text-ios-dark-red'}`}>
                      {networkStatus.online ? t('v3/developerSettings:networkTest.online') : t('v3/developerSettings:networkTest.offline')}
                    </span>
                  </div>
                  <div>
                    <span className="text-ios-gray dark:text-ios-dark-gray">{t('v3/developerSettings:networkTest.successCount')}{t('v3/developerSettings:common.units.colon')}</span>
                    <span className="font-medium ml-1">{networkStatus.successCount}/{networkStatus.totalTests}</span>
                  </div>
                </div>

                {lastNetworkCheck && (
                  <div className="mb-3">
                    <span className="text-ios-gray dark:text-ios-dark-gray">{t('v3/developerSettings:networkTest.lastTest')}{t('v3/developerSettings:common.units.colon')}</span>
                    <span className="font-medium ml-1 text-ios-footnote">{lastNetworkCheck.toLocaleString()}</span>
                  </div>
                )}

                {/* 详细测试结果 */}
                <div>
                  <h4 className="text-ios-subheadline font-medium mb-2">{t('v3/developerSettings:networkTest.testResults')}</h4>
                  <div className="space-y-1">
                    {networkStatus.results.map((result, index) => (
                      <div key={index} className="flex items-center justify-between text-ios-footnote">
                        <span className="text-ios-gray dark:text-ios-dark-gray">{result.url}</span>
                        <span className={`font-medium ${result.success ? 'text-ios-green dark:text-ios-dark-green' : 'text-ios-red dark:text-ios-dark-red'}`}>
                          {result.success ? t('v3/developerSettings:networkTest.success') : t('v3/developerSettings:networkTest.failed')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 自定义 URL 测试 */}
          <div>
            <h3 className="text-ios-headline font-medium mb-3">{t('v3/developerSettings:networkTest.testConnection')}</h3>
            <div className="flex gap-2 mb-3">
              <input
                type="url"
                value={testUrl}
                onChange={(e) => setTestUrl(e.target.value)}
                placeholder={t('v3/developerSettings:networkTest.urlPlaceholder')}
                className="flex-1 px-3 py-2 border border-ios-gray5 dark:border-ios-dark-gray5 rounded-ios-md bg-white dark:bg-ios-dark-gray5 text-ios-body focus:outline-none focus:ring-2 focus:ring-ios-blue dark:focus:ring-ios-dark-blue"
              />
              <button
                onClick={handleTestConnection}
                disabled={isTestingConnection || !isElectron}
                className="px-4 py-2 bg-ios-green dark:bg-ios-dark-green text-white rounded-ios-md text-ios-subhead font-medium disabled:opacity-50"
              >
                {isTestingConnection ? t('v3/developerSettings:networkTest.testing') : t('v3/developerSettings:networkTest.testConnection')}
              </button>
            </div>

            {lastTestResult && (
              <div className="bg-ios-gray6 dark:bg-ios-dark-gray5 rounded-ios-md p-3">
                <h4 className="text-ios-subheadline font-medium mb-2">{t('v3/developerSettings:networkTest.testResult')}</h4>
                <div className="space-y-2 text-ios-subhead">
                  <div>
                    <span className="text-ios-gray dark:text-ios-dark-gray">{t('v3/developerSettings:networkTest.testUrl')}{t('v3/developerSettings:common.units.colon')}</span>
                    <span className="font-medium ml-1 break-all">{lastTestResult.url}</span>
                  </div>
                  <div>
                    <span className="text-ios-gray dark:text-ios-dark-gray">{t('v3/developerSettings:networkTest.testResult')}{t('v3/developerSettings:common.units.colon')}</span>
                    <span className={`font-medium ml-1 ${lastTestResult.success ? 'text-ios-green dark:text-ios-dark-green' : 'text-ios-red dark:text-ios-dark-red'}`}>
                      {lastTestResult.success ? t('v3/developerSettings:networkTest.success') : t('v3/developerSettings:networkTest.failed')}
                    </span>
                  </div>
                  {lastTestResult.success && lastTestResult.status && (
                    <div>
                      <span className="text-ios-gray dark:text-ios-dark-gray">{t('v3/developerSettings:networkTest.status')}{t('v3/developerSettings:common.units.colon')}</span>
                      <span className="font-medium ml-1">{lastTestResult.status} {lastTestResult.statusText}</span>
                    </div>
                  )}
                  {!lastTestResult.success && lastTestResult.error && (
                    <div>
                      <span className="text-ios-gray dark:text-ios-dark-gray">{t('v3/developerSettings:networkTest.error')}{t('v3/developerSettings:common.units.colon')}</span>
                      <span className="font-medium ml-1 text-ios-red dark:text-ios-dark-red">{lastTestResult.error}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-ios-gray dark:text-ios-dark-gray">{t('v3/developerSettings:networkTest.timestamp')}{t('v3/developerSettings:common.units.colon')}</span>
                    <span className="font-medium ml-1 text-ios-footnote">{new Date(lastTestResult.timestamp).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 语音识别设置部分 */}
      <div className="mb-6">
        <h2 className="text-ios-title3 font-semibold mb-2">{t('v3/developerSettings:whisperSettings.title')}</h2>
        <div className="bg-white dark:bg-ios-dark-gray6 rounded-ios-lg overflow-hidden border border-ios-gray5 dark:border-ios-dark-gray5 shadow-ios dark:shadow-ios-dark p-4 mb-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-ios-headline font-semibold">{t('v3/developerSettings:whisperSettings.forceCurrentLanguage')}</h3>
              <p className="text-ios-subhead text-ios-gray dark:text-ios-dark-gray">
                {forceWhisperLanguage
                  ? t('v3/developerSettings:whisperSettings.forceLanguageDescription')
                  : t('v3/developerSettings:whisperSettings.autoDetectDescription')}
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={forceWhisperLanguage}
                onChange={toggleWhisperLanguage}
              />
              <div className="w-11 h-6 bg-ios-gray5 peer-focus:ring-4 peer-focus:ring-ios-blue/20 dark:peer-focus:ring-ios-dark-blue/20 rounded-full peer dark:bg-ios-dark-gray4 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-ios-blue dark:peer-checked:bg-ios-dark-blue"></div>
            </label>
          </div>
        </div>
      </div>

      {/* 个人访问令牌部分 */}
      <div className="mb-6">
        <h2 className="text-ios-title3 font-semibold mb-2">{t('v3/me:developer.personalTokens')}</h2>
        <p className="text-ios-body text-ios-gray dark:text-ios-dark-gray mb-4">
          {t('v3/me:developer.tokenDescription')}
        </p>

        {/* 创建新令牌 */}
        <div className="bg-white dark:bg-ios-dark-gray6 rounded-ios-lg overflow-hidden border border-ios-gray5 dark:border-ios-dark-gray5 shadow-ios dark:shadow-ios-dark p-4 mb-4">
          <h3 className="text-ios-headline font-semibold mb-2">{t('v3/me:developer.createToken')}</h3>
          <div className="flex mb-4">
            <input
              type="text"
              value={newTokenName}
              onChange={(e) => setNewTokenName(e.target.value)}
              placeholder={t('v3/me:developer.tokenNamePlaceholder')}
              className="flex-1 px-3 py-2 border border-ios-gray5 dark:border-ios-dark-gray5 rounded-ios-md bg-white dark:bg-ios-dark-gray5 text-ios-body focus:outline-none focus:ring-2 focus:ring-ios-blue dark:focus:ring-ios-dark-blue"
            />
            <button
              onClick={handleCreateToken}
              disabled={creatingToken || !newTokenName.trim()}
              className="ml-2 px-4 py-2 bg-ios-blue dark:bg-ios-dark-blue text-white rounded-ios-md font-medium disabled:opacity-50"
            >
              {creatingToken ? t('v3/me:developer.creating') : t('v3/me:developer.create')}
            </button>
          </div>

          {/* 新创建的令牌 */}
          {newToken && (
            <div className="mt-4 p-3 bg-ios-gray6 dark:bg-ios-dark-gray5 rounded-ios-md">
              <div className="flex justify-between items-center mb-2">
                <h4 className="text-ios-subhead font-medium">{t('v3/me:developer.newToken')}</h4>
                <button
                  onClick={() => copyToClipboard(newToken.token)}
                  className="text-ios-blue dark:text-ios-dark-blue text-ios-subhead"
                >
                  {t('v3/me:developer.copy')}
                </button>
              </div>
              <p className="text-ios-footnote break-all font-mono bg-ios-gray5 dark:bg-ios-dark-gray4 p-2 rounded-ios-sm">
                {newToken.token}
              </p>
              <p className="text-ios-caption text-ios-red dark:text-ios-dark-red mt-2">
                {t('v3/me:developer.tokenWarning')}
              </p>
            </div>
          )}
        </div>

        {/* 令牌列表 */}
        <div className="bg-white dark:bg-ios-dark-gray6 rounded-ios-lg overflow-hidden border border-ios-gray5 dark:border-ios-dark-gray5 shadow-ios dark:shadow-ios-dark">
          <h3 className="text-ios-headline font-semibold p-4 border-b border-ios-gray5 dark:border-ios-dark-gray5">
            {t('v3/me:developer.yourTokens')}
          </h3>

          {loading ? (
            <div className="p-4 text-center text-ios-gray dark:text-ios-dark-gray">
              {t('v3/me:developer.loadingText')}
            </div>
          ) : tokens.length === 0 ? (
            <div className="p-4 text-center text-ios-gray dark:text-ios-dark-gray">
              {t('v3/me:developer.noTokens')}
            </div>
          ) : (
            <ul className="divide-y divide-ios-gray5 dark:divide-ios-dark-gray5">
              {tokens.map(token => (
                <li key={token.id} className="p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <h4 className="text-ios-body font-medium">{token.name}</h4>
                      <p className="text-ios-footnote text-ios-gray dark:text-ios-dark-gray">
                        {t('v3/me:developer.lastUsed')}: {formatDate(token.lastUsedAt)}
                      </p>
                      <p className="text-ios-footnote text-ios-gray dark:text-ios-dark-gray">
                        {t('v3/me:developer.created')}: {formatDate(token.createdAt)}
                      </p>
                    </div>
                    <button
                      onClick={() => handleDeleteToken(token.id)}
                      disabled={deletingTokenId === token.id}
                      className="px-3 py-1 bg-ios-red dark:bg-ios-dark-red text-white rounded-ios-md text-ios-subhead font-medium disabled:opacity-50"
                    >
                      {deletingTokenId === token.id ? t('v3/me:developer.deleting') : t('v3/me:developer.delete')}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Tempo 协议链接测试 */}
      <div className="mb-6">
        <h2 className="text-ios-title3 font-semibold mb-2">{t('v3/developerSettings:tempoProtocolTest.title')}</h2>
        <div className="bg-white dark:bg-ios-dark-gray6 rounded-ios-lg overflow-hidden border border-ios-gray5 dark:border-ios-dark-gray5 shadow-ios dark:shadow-ios-dark p-4">
          <p className="text-ios-subhead text-ios-gray dark:text-ios-dark-gray mb-2">
            {t('v3/developerSettings:tempoProtocolTest.description')}
          </p>

          {/* 数据状态显示 */}
          <div className="mb-4 p-3 bg-ios-gray6 dark:bg-ios-dark-gray5 rounded-ios-md">
            <div className="text-ios-caption1 text-ios-gray dark:text-ios-dark-gray">
              <span className="font-medium">{t('v3/developerSettings:tempoProtocolTest.currentData')}{t('v3/developerSettings:common.units.colon')}</span>
              <span className="ml-2">{t('v3/developerSettings:tempoProtocolTest.projects')} {projects.length}{t('v3/developerSettings:common.units.count')}</span>
              <span className="ml-2">{t('v3/developerSettings:tempoProtocolTest.tasks')} {tasks.length}{t('v3/developerSettings:common.units.count')}</span>
              {projects.length === 0 && tasks.length === 0 && (
                <span className="ml-2 text-ios-orange dark:text-ios-dark-orange">
                  {t('v3/developerSettings:tempoProtocolTest.createFirst')}
                </span>
              )}
            </div>
          </div>

          {/* 测试消息容器 */}
          <div className="space-y-4">
            {generateTestMessages().map((message, index) => (
              <div key={message.id} className="border border-ios-gray5 dark:border-ios-dark-gray5 rounded-ios-md p-3">
                <h4 className="text-ios-subheadline font-medium mb-2">
                  {index === 0 && projects.length > 0 ? t('v3/developerSettings:tempoProtocolTest.realProjectLinkTest') :
                   index === 1 && tasks.length > 0 ? t('v3/developerSettings:tempoProtocolTest.realTaskLinkTest') :
                   index === 2 ? t('v3/developerSettings:tempoProtocolTest.mixedLinkTest') :
                   t('v3/developerSettings:tempoProtocolTest.sampleLinkTest')}
                </h4>
                <ChatMessage
                  message={message}
                  onToggleExpand={() => {}}
                />
              </div>
            ))}

            {/* 无效链接测试 - 始终显示 */}
            <div className="border border-ios-gray5 dark:border-ios-dark-gray5 rounded-ios-md p-3">
              <h4 className="text-ios-subheadline font-medium mb-2">{t('v3/developerSettings:tempoProtocolTest.invalidLinkTest')}</h4>
              <ChatMessage
                message={{
                  id: 'test-invalid-1',
                  role: 'assistant',
                  content: '这是一个无效的 tempo 链接：[无效链接](tempo://invalid/type/123) 应该显示为普通文本。',
                  conversationId: 'test-conv',
                  timestamp: new Date().toISOString(),
                }}
                onToggleExpand={() => {}}
              />
            </div>
          </div>
        </div>
      </div>

      {/* MCP 配置示例 */}
      <div className="mb-6">
        <h2 className="text-ios-title3 font-semibold mb-2">{t('v3/me:developer.mcpConfig')}</h2>
        <div className="bg-white dark:bg-ios-dark-gray6 rounded-ios-lg overflow-hidden border border-ios-gray5 dark:border-ios-dark-gray5 shadow-ios dark:shadow-ios-dark p-4">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-ios-headline font-semibold">{t('v3/me:developer.mcpExample')}</h3>
            <button
              onClick={() => copyToClipboard(mcpConfigExample)}
              className="text-ios-blue dark:text-ios-dark-blue text-ios-subhead"
            >
              {t('v3/me:developer.copy')}
            </button>
          </div>
          <pre className="text-ios-footnote overflow-x-auto p-3 bg-ios-gray6 dark:bg-ios-dark-gray5 rounded-ios-md font-mono">
            {mcpConfigExample}
          </pre>
        </div>
      </div>

      {/* 缓存管理部分 */}
      <div className="mb-6">
        <h2 className="text-ios-title3 font-semibold mb-2">{t('v3/me:developer.cacheManagement')}</h2>

        {/* PWA 缓存管理 */}
        <div className="bg-white dark:bg-ios-dark-gray6 rounded-ios-lg overflow-hidden border border-ios-gray5 dark:border-ios-dark-gray5 shadow-ios dark:shadow-ios-dark p-4 mb-4">
          <h3 className="text-ios-headline font-semibold mb-3">{t('v3/me:developer.pwaCache')}</h3>

          {lastChecked && (
            <p className="text-ios-footnote text-ios-gray dark:text-ios-dark-gray mb-3">
              {t('v0/common:pwaVersion.lastChecked')}: {lastChecked.toLocaleString()}
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleCheckUpdate}
              disabled={isCheckingUpdate}
              className="px-3 py-2 bg-ios-blue dark:bg-ios-dark-blue text-white rounded-ios-md text-ios-subhead font-medium disabled:opacity-50"
            >
              {isCheckingUpdate ? t('v3/me:developer.checking') : t('v3/me:developer.checkUpdate')}
            </button>

            <button
              onClick={handleForceUpdate}
              className="px-3 py-2 bg-ios-teal dark:bg-ios-dark-teal text-white rounded-ios-md text-ios-subhead font-medium"
            >
              {t('v3/me:developer.forceUpdate')}
            </button>

            <button
              onClick={() => openConfirmDialog('clearCache')}
              className="px-3 py-2 bg-ios-orange dark:bg-ios-dark-orange text-white rounded-ios-md text-ios-subhead font-medium"
            >
              {t('v3/me:developer.clearCache')}
            </button>
          </div>
        </div>

        {/* localStorage 管理 */}
        <div className="bg-white dark:bg-ios-dark-gray6 rounded-ios-lg overflow-hidden border border-ios-gray5 dark:border-ios-dark-gray5 shadow-ios dark:shadow-ios-dark p-4 mb-4">
          <h3 className="text-ios-headline font-semibold mb-3">{t('v3/me:developer.localStorage')}</h3>

          <div className="mb-4">
            <button
              onClick={() => openConfirmDialog('clearLocalStorage')}
              className="px-3 py-2 bg-ios-red dark:bg-ios-dark-red text-white rounded-ios-md text-ios-subhead font-medium"
            >
              {t('v3/me:developer.clearAllLocalStorage')}
            </button>
          </div>

          <h4 className="text-ios-subheadline font-medium mb-2">{t('v3/me:developer.clearSpecificStorage')}</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <button
              onClick={() => openConfirmDialog('clearSpecific', 'v3_conversations')}
              className="px-3 py-2 bg-ios-gray5 dark:bg-ios-dark-gray4 text-ios-black dark:text-white rounded-ios-md text-ios-subhead font-medium text-left"
            >
              {t('v3/me:developer.clearConversations')}
            </button>

            <button
              onClick={() => openConfirmDialog('clearSpecific', 'v3_messages')}
              className="px-3 py-2 bg-ios-gray5 dark:bg-ios-dark-gray4 text-ios-black dark:text-white rounded-ios-md text-ios-subhead font-medium text-left"
            >
              {t('v3/me:developer.clearMessages')}
            </button>

            <button
              onClick={() => openConfirmDialog('clearSpecific', 'v3_chatScrollPositions')}
              className="px-3 py-2 bg-ios-gray5 dark:bg-ios-dark-gray4 text-ios-black dark:text-white rounded-ios-md text-ios-subhead font-medium text-left"
            >
              {t('v3/me:developer.clearUIState')}
            </button>

            <button
              onClick={() => openConfirmDialog('clearSpecific', 'v3_oldestMessageId')}
              className="px-3 py-2 bg-ios-gray5 dark:bg-ios-dark-gray4 text-ios-black dark:text-white rounded-ios-md text-ios-subhead font-medium text-left"
            >
              {t('v3/me:developer.clearMessages')} ID
            </button>
          </div>
        </div>
      </div>

      {/* 确认对话框 */}
      <AlertDialog
        isOpen={isConfirmDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setIsConfirmDialogOpen(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('v3/me:developer.confirm')}
            </AlertDialogHeader>

            <AlertDialogBody>
              {confirmAction === 'clearCache' && t('v3/me:developer.confirmClearCache')}
              {confirmAction === 'clearLocalStorage' && t('v3/me:developer.confirmClearLocalStorage')}
              {confirmAction === 'clearSpecific' && t('v3/me:developer.confirmClearSpecificStorage')}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setIsConfirmDialogOpen(false)}>
                {t('v0/common:cancel')}
              </Button>
              <Button colorScheme="red" onClick={handleConfirmAction} ml={3}>
                {t('v3/me:developer.confirm')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </SettingsPageLayout>
  );
};

export default DeveloperSettings;
